/**
 * What Standout Review sends to the family-repeat judge, and the key it
 * stores so a reload can skip the model. The same function runs again
 * on the moments the save re-reads, before the student transaction.
 *
 * The set is one student's family-facing notes: not secret, not
 * don't-share, with a parent sentence. Shared and unshared notes are in
 * the same set and every one is judged. Share state is not in the key, so
 * sharing or unsharing does not ask the model again.
 */
import { parentShareText } from "#utils/standoutMomentMigration"

export const FAMILY_REPEAT_MESSAGE_CAP = 40
export const FAMILY_REPEAT_TEXT_CAP = 1000
const KEY_VERSION = "v3"

export type FamilyRepeatMoment = {
  id: string
  studentId: string
  category?: string | null
  dontShare?: boolean | null
  familyState?: "pending" | "approved" | "sent" | null
  isShared?: boolean | null
  note?: string | null
  generatedNote?: string | null
  familyNoteSource?: string | null
  createdAt?: string | null
  lastUpdatedAt?: string | null
}

export type FamilyRepeatSide = {
  id: string
  text: string
}

export type FamilyRepeatInput = {
  key: string
  messages: FamilyRepeatSide[]
  momentIds: string[]
}

/** What a conditional family-repeat write did with the student doc. */
export type FamilyRepeatWriteResult =
  | { status: "saved" | "current" }
  | {
      status: "stale"
      storedKey: string | null
      storedIds: readonly string[] | null
    }

const clip = (text: string): string =>
  text.trim().slice(0, FAMILY_REPEAT_TEXT_CAP)

const createdMs = (moment: FamilyRepeatMoment): number => {
  const parsed = Date.parse(moment.createdAt ?? "")
  return Number.isNaN(parsed) ? 0 : parsed
}

const byNewest = (a: FamilyRepeatMoment, b: FamilyRepeatMoment): number => {
  const at = createdMs(b) - createdMs(a)
  if (at !== 0) return at
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

const byId = (a: FamilyRepeatSide, b: FamilyRepeatSide): number =>
  a.id < b.id ? -1 : a.id > b.id ? 1 : 0

/** Text a family could get. Secrets and don't-share never reach the model. */
export const isFamilyRepeatNote = (moment: FamilyRepeatMoment): boolean => {
  if (moment.category === "secret" || moment.dontShare === true) return false
  return clip(parentShareText(moment)).length > 0
}

/** FNV-1a over UTF-16 units, with the length. Note text stays out of the key. */
const textFingerprint = (text: string): string => {
  let hash = 0x811c9dc5
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `${text.length.toString(36)}.${hash.toString(36)}`
}

/** One key line per moment. The id is encoded, so a line never holds "\n". */
const keyRow = (row: FamilyRepeatSide): string =>
  `${encodeURIComponent(row.id)}:${textFingerprint(row.text)}`

const keyRows = (key: string | null | undefined): Set<string> | null => {
  if (!key?.startsWith(`${KEY_VERSION}\n`)) return null
  return new Set(key.split("\n").slice(1))
}

/**
 * One student's family-facing notes, newest first up to the cap.
 * Null when there are not two notes to compare.
 */
export const familyRepeatInputFor = (
  moments: readonly FamilyRepeatMoment[]
): FamilyRepeatInput | null => {
  const messages = moments
    .filter(isFamilyRepeatNote)
    .sort(byNewest)
    .slice(0, FAMILY_REPEAT_MESSAGE_CAP)
    .map((moment) => ({
      id: moment.id,
      text: clip(parentShareText(moment)),
    }))
    .sort(byId)
  if (messages.length < 2) return null
  // No leading or trailing whitespace. cleanFormData trims every string
  // on the way to Firestore, and the stored key must still match this one.
  // A v2 judgment (unshared against sent) never matches, so that student
  // is judged once and then kept.
  return {
    key: [KEY_VERSION, ...messages.map(keyRow)].join("\n"),
    messages,
    momentIds: messages.map((row) => row.id),
  }
}

/**
 * True when every note in `inputKey` was judged with the same text under
 * `storedKey`. A note that aged out of the window, was deleted, or became
 * secret or don't-share leaves a smaller set the stored judgment still
 * answers, so the review does not ask the model again.
 */
export const familyRepeatKeyCovers = (
  storedKey: string | null | undefined,
  inputKey: string
): boolean => {
  if (storedKey === inputKey) return true
  const stored = keyRows(storedKey)
  const input = keyRows(inputKey)
  if (!stored || !input) return false
  for (const row of input) {
    if (!stored.has(row)) return false
  }
  return true
}

/**
 * True when `storedIds` is a smaller moment set inside `inputIds`.
 * Equal lists and a missing list are not covered. Ids come from the
 * judgment's moment list, never from splitting the key.
 */
export const familyRepeatIdsAreCoveredBy = (
  storedIds: readonly string[] | null | undefined,
  inputIds: readonly string[]
): boolean => {
  if (!storedIds || storedIds.length === 0) return false
  if (inputIds.length <= storedIds.length) return false
  const input = new Set(inputIds)
  return storedIds.every((id) => input.has(id))
}

/**
 * True when the stored key must be left alone.
 * A null key, this job's own key, the key this job started from, and a
 * smaller moment-id list (only when this job also started from nothing)
 * can all be replaced. Coverage uses the id list, not the key text.
 */
export const familyRepeatWriteIsStale = (
  storedKey: string | null,
  expectedKey: string,
  observedKey: string | null,
  storedIds: readonly string[] | null,
  expectedIds: readonly string[]
): boolean => {
  if (storedKey === null || storedKey === expectedKey) return false
  if (storedKey === observedKey) return false
  return !(
    observedKey === null && familyRepeatIdsAreCoveredBy(storedIds, expectedIds)
  )
}

export const familyRepeatInputsByStudent = (
  moments: readonly FamilyRepeatMoment[]
): Map<string, FamilyRepeatInput> => {
  const byStudent = new Map<string, FamilyRepeatMoment[]>()
  for (const moment of moments) {
    const group = byStudent.get(moment.studentId)
    if (group) group.push(moment)
    else byStudent.set(moment.studentId, [moment])
  }
  const inputs = new Map<string, FamilyRepeatInput>()
  for (const [studentId, group] of byStudent) {
    const input = familyRepeatInputFor(group)
    if (input) inputs.set(studentId, input)
  }
  return inputs
}

/**
 * Similar ids per moment, both ways: when A names B, B also lists A.
 * Only ids in `momentIds` count. A moment with no similar note is absent.
 */
export const familyRepeatSimilarIds = (
  verdicts: readonly {
    momentId: string
    repeats: boolean
    similarMomentIds?: readonly string[] | null
  }[],
  momentIds: readonly string[]
): ReadonlyMap<string, readonly string[]> => {
  const inSet = new Set(momentIds)
  const pairs = new Map<string, Set<string>>()
  const link = (from: string, to: string) => {
    const group = pairs.get(from)
    if (group) group.add(to)
    else pairs.set(from, new Set([to]))
  }
  for (const verdict of verdicts) {
    if (!verdict.repeats || !inSet.has(verdict.momentId)) continue
    for (const id of verdict.similarMomentIds ?? []) {
      if (!inSet.has(id) || id === verdict.momentId) continue
      link(verdict.momentId, id)
      link(id, verdict.momentId)
    }
  }
  const similar = new Map<string, readonly string[]>()
  for (const id of momentIds) {
    const group = pairs.get(id)
    if (group) similar.set(id, [...group].sort())
  }
  return similar
}
